import React, { useEffect, useRef } from 'react';
import { Modal, DialogContent } from '@material-ui/core';
import { isEqual, isEmpty, pickBy } from 'lodash-es';
import { useSetState } from '~/app/Utility/customHooks';
import {
  findIndex,
  remainingSignaturesNeeded,
  withoutSignaturesOnFile,
  handleSignatureRequestOption,
} from '~/app/Pages/Returns/pages/Signatures/signaturesHelper.js';
import { SIGNEE, SIGNEE_TYPE } from '~/app/constants.js';
import SendSignature from '~/app/Pages/Returns/pages/Signatures/components/SendSignature.jsx';
import XlinkAPI from '~/app/api/xlinkAPI.js';
import { statusOK } from '~/app/webHelpers.js';
import ErrorHelpers from '~/app/errorHelpers.js';
import { useSelector, useDispatch } from 'react-redux';
import { actions as appActions } from '~/app/redux/modules/app';
import { actions as formViewerActions } from '~/app/redux/modules/formViewer.js';

const RemoteSignatureModal = ({
  handleSigningRemotely,
  handleSigningInOffice,
  preparerInfo,
  drilldownHistory,
  signatureRequestDataRef,
  returnPrintQueryLoading,
}) => {
  const {
    isSendSignatureModalActive,
    signaturesNeeded,
    initialSigneeData,
    amendedPreparerData,
    taxpayerInformation,
    spouseInformation,
    returnID,
    taxpayerElectronicDisclosure,
    spouseElectronicDisclosure,
    businessElectronicDisclosure,
  } = useSelector(state => ({
    isSendSignatureModalActive: state.remoteSign.isSendSignatureModalActive,
    signaturesNeeded: state.remoteSign.signaturesNeeded,
    initialSigneeData: state.remoteSign.initialSigneeData,
    amendedPreparerData: state.remoteSign.amendedPreparerData,
    taxpayerInformation: state.formViewer.taxpayerInformation,
    spouseInformation: state.formViewer.spouseInformation,
    returnID: state.returnProfile.returnID,
    taxpayerElectronicDisclosure: state.formViewer.taxpayerElectronicDisclosure,
    spouseElectronicDisclosure: state.formViewer.spouseElectronicDisclosure,
    businessElectronicDisclosure: state.formViewer.businessElectronicDisclosure,
  }));

  const dispatch = useDispatch();
  const isSendingRef = useRef(false);

  const [state, setState] = useSetState({
    signeeData: {},
    signaturesCollected: {},
    isAllSignaturesCollected: false,
    canUseCellTP: false,
    canUseCellSP: false,
    currentSigDoc: 0,
  });

  useEffect(() => {
    if (isSendSignatureModalActive) {
      fetchCellPhoneData();
      signatureRequestDataRef.current = [];
      setState({ signaturesCollected: {} });
      isSendingRef.current = false;
    } else if (!isSendingRef.current) {
      dispatch(formViewerActions.signatureRequestActiveSet(false));
    }
  }, [isSendSignatureModalActive]);

  useEffect(() => {
    if (initialSigneeData.signee) {
      handleSigneeData(initialSigneeData.signee);
    }
  }, [taxpayerInformation, spouseInformation, initialSigneeData, amendedPreparerData]);

  const fetchCellPhoneData = async () => {
    try {
      const res = await XlinkAPI.getTextMessageClientInfo(returnID);

      if (statusOK(res)) {
        if (hasValidTextLinkFields(res.data?.[0])) {
          setState({ canUseCellTP: true });
          dispatch(formViewerActions.textLinkClientInfoFetched(res.data));
        } else {
          setState({ canUseCellTP: false });
        }

        if (res.data?.[1] && hasValidTextLinkFields(res.data?.[1])) {
          setState({ canUseCellSP: true });
          dispatch(formViewerActions.textLinkClientInfoFetched(res.data));
        } else {
          setState({ canUseCellSP: false });
        }
      }
    } catch (error) {
      dispatch(
        appActions.showSnackbarMessage(error, 'error', 3500, {
          vertical: 'top',
          horizontal: 'center',
        }),
      );
      ErrorHelpers.handleError('Error fetching message info', error);
    }
  };

  const hasValidTextLinkFields = returnInfo => {
    return (
      returnInfo?.cellPhone?.length > 8 && returnInfo?.cellCarrier && returnInfo?.carrierDomain
    );
  };

  const handleSigneeData = documentSignee => {
    const {
      firstName: taxpayerFirstName,
      lastName: taxpayerLastName,
      cellPhone: taxpayerCellPhone,
      email: taxpayerEmail,
    } = { ...taxpayerInformation };
    const {
      firstName: spouseFirstName,
      lastName: spouseLastName,
      cellPhone: spouseCellPhone,
      email: spouseEmail,
    } = { ...spouseInformation };

    const { TAXPAYER, TAXPAYER_SECOND, SPOUSE, SPOUSE_SECOND, AMENDED_PREPARER, OFFICER } = SIGNEE;
    let signeeType, signee, cell, email;

    switch (documentSignee) {
      case TAXPAYER:
      case TAXPAYER_SECOND:
        signeeType = documentSignee === TAXPAYER ? TAXPAYER : TAXPAYER_SECOND;
        signee = `Taxpayer ${
          documentSignee === TAXPAYER_SECOND ? 'Second' : ''
        } (${taxpayerFirstName} ${taxpayerLastName})`;
        cell = taxpayerCellPhone;
        email = taxpayerEmail;
        break;
      case SPOUSE:
      case SPOUSE_SECOND:
        signeeType = documentSignee === SPOUSE ? SPOUSE : SPOUSE_SECOND;
        signee = `Spouse ${
          documentSignee === SPOUSE_SECOND ? 'Second' : ''
        }  (${spouseFirstName} ${spouseLastName})`;
        cell = spouseCellPhone;
        email = spouseEmail;
        break;
      case AMENDED_PREPARER:
        signeeType = AMENDED_PREPARER;
        signee = `Amended Preparer (${
          amendedPreparerData?.contactData?.preparer_name || 'Unknown'
        })`;
        cell = amendedPreparerData?.contactData?.preparer_cell;
        email = amendedPreparerData?.contactData?.preparer_email;
        break;
      case OFFICER:
        signeeType = OFFICER;
        signee = `Business (${taxpayerInformation?.BusinessName || 'Unknown'})`;
        cell = taxpayerCellPhone;
        email = taxpayerEmail;
        break;
    }
    setState({
      signeeData: {
        signeeType,
        signee,
        cell,
        email,
      },
    });
  };

  const allSignaturesCollected = () => {
    // Compare against the same filtered set used to pick the next signee,
    // otherwise a signee skipped for already having a signature on file would
    // never be "collected" and the flow could never complete.
    const isAllSignaturesCollected = isEqual(
      state.signaturesCollected,
      pickBy(withoutSignaturesOnFile(signaturesNeeded, amendedPreparerData)),
    );
    setState({
      signeeData: { ...state.signeeData, isAllSignaturesCollected },
    });
  };

  const handleNextSignatureNeeded = () => {
    if (!state.signeeData.isAllSignaturesCollected) {
      const nextSignatureNeeded = Object.keys(
        remainingSignaturesNeeded(
          withoutSignaturesOnFile(signaturesNeeded, amendedPreparerData),
          state.signaturesCollected,
        ),
      );
      handleSigneeData(nextSignatureNeeded[0]);
    }
  };

  const handleAddingRequestData = (data, signeeType) => {
    const index = findIndex(signatureRequestDataRef.current, signeeType);
    let signeeInformation = {};

    if (data.signingMethod === 'Remote Signature') {
      dispatch(formViewerActions.setRMSflag(true));
    } else if (data.signingMethod === 'In-Office Device Signature') {
      dispatch(formViewerActions.setRMSflag(true));
      dispatch(formViewerActions.setIODflag(true));
    }

    switch (signeeType) {
      case SIGNEE.TAXPAYER:
        signeeInformation = {
          ...taxpayerInformation,
          signeeType: SIGNEE_TYPE.TAXPAYER,
        };
        break;
      case SIGNEE.TAXPAYER_SECOND:
        signeeInformation = {
          ...taxpayerInformation,
          signeeType: SIGNEE_TYPE.TAXPAYER_SECOND_SIGNATURE,
        };
        break;
      case SIGNEE.SPOUSE:
        signeeInformation = {
          ...spouseInformation,
          signeeType: SIGNEE_TYPE.SPOUSE,
        };
        break;
      case SIGNEE.SPOUSE_SECOND:
        signeeInformation = {
          ...spouseInformation,
          signeeType: SIGNEE_TYPE.SPOUSE_SECOND_SIGNATURE,
        };
        break;
      case SIGNEE.AMENDED_PREPARER:
        signeeInformation = {
          ...amendedPreparerData,
          signeeType: SIGNEE_TYPE.AMENDED_PAID_PREPARER_SIGNATURE,
        };
        break;
      case SIGNEE.OFFICER:
        signeeInformation = {
          ...taxpayerInformation,
          signeeType: SIGNEE_TYPE.OFFICER,
        };
        break;
    }

    const hasMatchingValue = signatureRequestDataRef.current.some(el => el.signee === data.signee);
    hasMatchingValue
      ? signatureRequestDataRef.current.splice(index, 1, Object.assign(data, signeeInformation))
      : signatureRequestDataRef.current.push(Object.assign(data, signeeInformation));
  };

  const handleSendRequest = () => {
    isSendingRef.current = true;
    handleSignatureRequestOption(
      signatureRequestDataRef.current,
      handleSigningRemotely,
      initialSigneeData.document,
      handleSigningInOffice,
      state.currentSigDoc,
    );
  };

  if (
    !isSendSignatureModalActive ||
    !initialSigneeData.signee ||
    (isEmpty(taxpayerInformation) && isEmpty(spouseInformation))
  ) {
    return null;
  }

  return (
    <Modal
      id="sendSignatureModal"
      style={{ position: 'absolute', top: '10%', height: '60em' }}
      open={isSendSignatureModalActive}
      disableBackdropClick={true}
    >
      <DialogContent>
        <SendSignature
          handleNextSignatureNeeded={handleNextSignatureNeeded}
          updateModalContent={handleSigneeData}
          addSignatureRequestData={handleAddingRequestData}
          signatureRequestData={signatureRequestDataRef.current}
          updateSignaturesCollected={(data, bool) => (state.signaturesCollected[data] = bool)}
          allSignaturesCollected={allSignaturesCollected}
          signeeData={state.signeeData}
          documentType={initialSigneeData.document}
          isLoading={false}
          preparerInfo={preparerInfo}
          returnPrintQueryLoading={returnPrintQueryLoading}
          sendRequest={handleSendRequest}
          canUseCellTP={state.canUseCellTP}
          canUseCellSP={state.canUseCellSP}
          taxpayerElectronicDisclosure={taxpayerElectronicDisclosure}
          spouseElectronicDisclosure={spouseElectronicDisclosure}
          businessElectronicDisclosure={businessElectronicDisclosure}
          drilldownHistory={drilldownHistory}
        />
      </DialogContent>
    </Modal>
  );
};

export default RemoteSignatureModal;
